import { useRevalidator } from '@remix-run/react';
import { Button } from 'flowbite-react';
import { useCallback, useMemo, useState } from 'react';
import { useLocale } from 'remix-i18next/react';
import { Icon } from '#common/components/Icon.tsx';
import { Tooltip } from '#common/components/Tooltip.tsx';
import { useEditableTranslation } from '#common/i18n/edit-mode.tsx';
import { useVoicePageRootData } from '#speaker-shop/hooks/useVoicePageRootData';
import { type VoiceData } from '#speaker-shop/models/speaker-voice-data.server.ts';
import { trackMediumSelection } from '#speaker-shop/utils/tracking.ts';
import { AudioProvider } from '../AudioContext';
import { AudioGeneration } from '../AudioGeneration';
import { Checkout } from '../Checkout';
import { ContactFormDialog } from '../ContactFormDialog';
import { Hero } from '../Hero';
import { MediumSelection } from '../MediumSelection';
import { useMediumSelectionContext } from '../MediumSelectionContext';
import { Pattern } from '../Pattern';
import { RecordingProvider } from '../RecordingContext';
import { Section } from '../Section';
import { SectionTitle } from '../SectionTitle';
import { VoiceDemoPlayer } from '../VoiceDemoPlayer';
import { type LayoutProps } from './types';

export function ShopLayout1(opts: LayoutProps) {
    const { selectedMediums, setSelectedMediums } = useMediumSelectionContext();
    const [ showContactForm, setShowContactForm ] = useState(false);
    const { t } = useEditableTranslation('hero');
    const locale = useLocale();
    const availableUses = useMemo(() => [ ...opts.availableUses, {
        id: 'api',
        title: t('medium-selection:api.title'),
        subTitle: t('medium-selection:api.subTitle'),
        icon: 'ant-design--api-filled',
        children: (
            <ShowApiContactFormButton
                shortName={opts.shortName}
                showContactForm={showContactForm}
                setShowContactForm={setShowContactForm}
            />
        )
    } ], [ opts.availableUses, opts.shortName, t, showContactForm, setShowContactForm ]);
    const revalidator = useRevalidator();
    const refreshData = useCallback(() => {
        revalidator.revalidate();
    }, [ revalidator ]);

    const onMediumSelected = (selectedMedium: string) => {
        if (selectedMedium !== 'api') {
            setSelectedMediums(prev => {
                const selected = prev.indexOf(selectedMedium) !== -1
                    ? prev.filter(x => x !== selectedMedium)
                    : [ ...prev, selectedMedium ];
                trackMediumSelection(selected, opts.availableUses);
                return selected;
            });
        } else {
            setShowContactForm(true);
        }
    };

    return (
        <RecordingProvider>
            <AudioProvider>
                <Hero testimonials={opts.testimonials}>
                    <VoiceDemoSection />
                </Hero>
                <Section className='bg-slate-100 md:bg-transparent' id='medium-selection'>
                    <MediumSelection
                        selectedMediums={selectedMediums}
                        onSelectedMediumChange={onMediumSelected}
                        availableMediums={availableUses}
                        currency={opts.currency}
                        availableCurrencies={opts.availableCurrencies}
                        locale={locale}
                    />
                </Section>
                <AudioGenerationContainer
                    hasConsent={opts.hasConsent}
                    isLoggedIn={opts.isLoggedIn}
                    anonymousId={opts.anonymousId}
                    limits={opts.limits}
                    defaultAudioControls={opts.defaultAudioControls}
                    requestDataRefresh={refreshData}
                />
                <Checkout availableUses={opts.availableUses} selectedUses={selectedMediums} currency={opts.currency} />
            </AudioProvider>
        </RecordingProvider>
    );
}

function VoiceDemoSection() {
    const rootData = useVoicePageRootData();
    const locale = useLocale();
    const { t } = useEditableTranslation('hero');
    const [ showContactForm, setShowContactForm ] = useState(false);
    const naturalSrc = locale === 'de' ? rootData.audioSampleDeHlsSrc : rootData.audioSampleEnHlsSrc;
    const cloneSrc = locale === 'de' ? rootData.cloneDemoDeHlsSrc : rootData.cloneDemoEnHlsSrc;

    if (!naturalSrc && !cloneSrc) {
        return null;
    }

    return (
        <div className='py-6 px-4'>
            <div className='flex flex-col max-w-xl mx-auto w-full'>
                <VoiceDemoPlayer naturalSrc={naturalSrc} cloneSrc={cloneSrc} />
                <div className='flex gap-4 w-full mt-4'>
                    <Button color='light' className='flex-1' onClick={() => setShowContactForm(true)}>
                        {t('voice-demo.book-natural')}
                    </Button>
                    <Button
                        color='light'
                        className='flex-1'
                        onClick={() =>
                            document.getElementById('medium-selection')?.scrollIntoView({ behavior: 'smooth' })}
                    >
                        {t('voice-demo.book-clone')}
                    </Button>
                </div>
            </div>
            <ContactFormDialog
                topic={'Contact real voice'}
                description={
                    <div>
                        <p>{t('common:contact-form-description')}</p>
                        <p>{t('common:contact-form-instruction')}</p>
                    </div>
                }
                open={showContactForm}
                onClose={() => setShowContactForm(false)}
            />
        </div>
    );
}

function ShowApiContactFormButton({ shortName, showContactForm, setShowContactForm }: {
    shortName: string;
    showContactForm: boolean;
    setShowContactForm: (value: boolean) => void;
}) {
    const { t } = useEditableTranslation();
    return (
        <>
            <Button color='yellow' onClick={() => setShowContactForm(true)} className='rounded'>
                {t('contact-speaker', { shortName })}
            </Button>
            <ContactFormDialog
                topic={'API'}
                description={
                    <div>
                        <p>{t('api-contact-form-description')}</p>
                        <p>{t('contact-form-instruction')}</p>
                    </div>
                }
                open={showContactForm}
                onClose={() => setShowContactForm(false)}
            />
        </>
    );
}

interface Opts {
    hasConsent: boolean;
    isLoggedIn: boolean;
    anonymousId: string | null;
    limits: {
        generations?: {
            max: number;
            used: number;
            remaining: number;
        };
        maxCharacters?: number;
    } | null;
    defaultAudioControls: VoiceData;
    requestDataRefresh: () => void;
}

function AudioGenerationContainer(opts: Opts) {
    const { t } = useEditableTranslation('audio-generation');
    return (
        <AudioGeneration {...opts}>
            {({
                renderTextArea,
                renderForm,
                characterCounter,
                characterLimitReached,
                remainingGenerations,
                consentOverlay,
                limitOverlay,
                voiceSettings,
                progressBar,
                autoplayBlocked,
                error,
                canGenerate,
                isPlayingAudio,
                blockedAndUnchanged,
                onGenerate,
                onStop,
            }) => (
                <div className='w-full bg-radial from-crispybluedark-600 to-crispybluedark-700 overflow-hidden relative z-0'>
                    <Section className='relative'>
                        <Pattern className='-z-1 absolute -top-32 left-0 w-full hidden md:block md:left-2/3 lg:right-auto' />
                        <SectionTitle
                            className='text-center md:text-start'
                            text={
                                <>
                                    {t('header')}
                                    <div className='text-lg mt-2 font-normal flex flex-row items-center leading-6'>
                                        <Icon
                                            icon='heroicons--information-circle'
                                            className='hidden md:block mr-2 shrink-0'
                                        />
                                        <div>{t('copy-protection-info')}</div>
                                    </div>
                                </>
                            }
                            color='white'
                        />
                        {consentOverlay.has || limitOverlay.has
                            ? (
                                <div className='absolute inset-0 z-10 bottom-0 top-0'>
                                    {consentOverlay.render()}
                                    {limitOverlay.render()}
                                </div>
                            )
                            : null}
                        {renderForm(
                            <div className='relative'>
                                <div className='grid grid-cols-1 grid-rows-1'>
                                    <div className='col-start-1 row-start-1'>
                                        {renderTextArea({
                                            rows: 10,
                                            className:
                                                'rounded-xl border-2 h-80 w-full bg-white p-8 text-3xl placeholder:text-3xl sm:placeholder:text-4xl md:placeholder:text-5xl placeholder:text-center placeholder:leading-60'
                                        })}
                                        {characterLimitReached.render()}
                                        <div className='grid-cols-3 items-center justify-center hidden md:grid'>
                                            {voiceSettings.render()}
                                            <div className='justify-self-center'>{characterCounter.render()}</div>
                                            <div className='justify-self-center'>{remainingGenerations.render()}</div>
                                        </div>

                                        <div className='md:hidden'>
                                            <div className='flex justify-between'>
                                                {characterCounter.render()}
                                                {remainingGenerations.render()}
                                            </div>
                                            <div className='justify-center flex mt-4'>{voiceSettings.render()}</div>
                                        </div>
                                    </div>
                                </div>

                                {error && (
                                    <div className='mt-8 flex justify-center w-full'>
                                        <div
                                            role='alert'
                                            className='w-full md:w-3/4 lg:w-1/2 rounded-lg border border-red-300 bg-red-50 text-red-800 px-4 py-3 flex items-start gap-3'
                                        >
                                            <Icon icon='heroicons--exclamation-triangle' className='mt-0.5 shrink-0' />
                                            <div>
                                                <p className='font-semibold'>{t('audio-generation:error-title')}</p>
                                                <p className='mt-1'>{error}</p>
                                                {blockedAndUnchanged && (
                                                    <p className='mt-1 text-red-700'>
                                                        {t('audio-generation:edit-to-continue')}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {progressBar.has && (
                                    <div className='absolute -left-4 -right-4 -bottom-4 -top-4 pt-8 backdrop-blur-sm rounded-t-2xl z-20'>
                                        <div className='mt-4'>{progressBar.render()}</div>
                                    </div>
                                )}

                                {autoplayBlocked.has && <div className='mt-4'>{autoplayBlocked.render()}</div>}

                                <div className='mt-10 flex justify-center w-full'>
                                    <div className='relative inline-block'>
                                        <div className='inline-grid relative'>
                                            <Button
                                                color='yellow'
                                                className='text-3xl w-70 h-16 relative col-start-1 row-start-1 z-10 rounded'
                                                type='button'
                                                onClick={isPlayingAudio ? onStop : onGenerate}
                                                disabled={isPlayingAudio ? false : !canGenerate}
                                            >
                                                <Icon
                                                    icon={isPlayingAudio ? 'fa--stop' : 'fa--magic'}
                                                    className='text-base mr-4 my-auto'
                                                >
                                                </Icon>
                                                <span>
                                                    {isPlayingAudio ? t('buttons.stop') : t('buttons.generate')}
                                                </span>
                                            </Button>
                                        </div>
                                        <div className='md:absolute md:top-1/2 md:left-full md:transform md:-translate-y-1/2 md:ml-6 mt-4 md:mt-0 text-white text-lg md:w-max flex justify-center'>
                                            <Tooltip
                                                trigger={
                                                    <div className='flex items-center cursor-help'>
                                                        <Icon icon='heroicons--information-circle' className='mr-2' />
                                                        {t('available-languages')}
                                                    </div>
                                                }
                                            >
                                                <div className='w-60 md:w-120'>{t('supported-languages')}</div>
                                            </Tooltip>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </Section>
                </div>
            )}
        </AudioGeneration>
    );
}
